import { Component, type ReactNode, type ErrorInfo } from 'react';
import { t } from '@/i18n';
import styles from './ErrorBoundary.module.css';
import { sanitizeText, trackError } from '@/shared/analytics/errors';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    trackError({
      source: 'errorBoundary',
      category: 'ui',
      error,
      context: {
        componentStack: sanitizeText(errorInfo.componentStack, 2000),
      },
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <h2>{t('errorBoundary.title')}</h2>
            <p>{t('errorBoundary.message')}</p>
            <button
              className={styles.button}
              onClick={() => window.location.reload()}
            >
              {t('errorBoundary.retry')}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
